import { z } from 'zod';
import { addDaysIso, zonedParts, zonedToUtc } from './time.js';
/**
 * My day repeats, time nudges and checklists (docs/adr/0107). Pure rules and date maths, shared by the API, the web, the
 * phone and the desktop note. Dates are 'YYYY-MM-DD' calendar dates in the person's own time zone, so the maths never
 * touches daylight saving; only `nudgeInstant` turns a date and time into an instant.
 */
const Interval = z.number().int().min(1).max(99);
const Weekday = z.number().int().min(0).max(6);
/** How a to-do repeats. Weekdays are 0 = Sunday … 6 = Saturday. */
export const RepeatRule = z
    .discriminatedUnion('freq', [
    /** Every N days ("every day" is N = 1). */
    z.object({ freq: z.literal('daily'), interval: Interval }),
    /** Monday to Friday. */
    z.object({ freq: z.literal('weekdays') }),
    /** Every N weeks on the chosen weekdays. */
    z.object({ freq: z.literal('weekly'), interval: Interval, days: z.array(Weekday).min(1).max(7) }),
    /** Every N months on a day of the month, or on the last day. */
    z.object({ freq: z.literal('monthly'), interval: Interval, day: z.union([z.number().int().min(1).max(31), z.literal('last')]) }),
    /** Every year on a month and day. */
    z.object({ freq: z.literal('yearly'), month: z.number().int().min(1).max(12), day: z.number().int().min(1).max(31) }),
])
    .refine((r) => r.freq !== 'weekly' || new Set(r.days).size === r.days.length, { message: 'Each weekday once' })
    .refine((r) => r.freq !== 'yearly' || r.day <= daysInMonth(2024, r.month), { message: 'That day does not exist in that month' });
// --- Calendar helpers ---------------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const parts = (date) => date.split('-').map(Number);
/** Days in a month (1-based), leap years included. */
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** 0 = Sunday … 6 = Saturday for a 'YYYY-MM-DD' date. */
export function weekdayOf(date) {
    return new Date(`${date}T00:00:00Z`).getUTCDay();
}
/** A day in a month, past the month's end landing on its last day (the 31st in April is the 30th). */
function clampDay(year, month, day) {
    const last = daysInMonth(year, month);
    return iso(year, month, day === 'last' ? last : Math.min(day, last));
}
function addMonths(year, month, n) {
    const total = year * 12 + (month - 1) + n;
    return [Math.floor(total / 12), (total % 12) + 1];
}
const isWeekday = (date) => {
    const w = weekdayOf(date);
    return w >= 1 && w <= 5;
};
/** Monday of the week a date is in (weeks start on Monday). */
const mondayOf = (date) => addDaysIso(date, -((weekdayOf(date) + 6) % 7));
// --- Occurrences --------------------------------------------------------------------------------
/** The rule's first date on or after `base` (when a repeat is set, the item moves there). */
export function firstOccurrence(rule, base) {
    const [y, m] = parts(base);
    switch (rule.freq) {
        case 'daily':
            return base;
        case 'weekdays': {
            let d = base;
            while (!isWeekday(d))
                d = addDaysIso(d, 1);
            return d;
        }
        case 'weekly': {
            let d = base;
            for (let i = 0; i < 7 && !rule.days.includes(weekdayOf(d)); i++)
                d = addDaysIso(d, 1);
            return d;
        }
        case 'monthly': {
            const here = clampDay(y, m, rule.day);
            if (here >= base)
                return here;
            const [ny, nm] = addMonths(y, m, 1);
            return clampDay(ny, nm, rule.day);
        }
        case 'yearly': {
            const here = clampDay(y, rule.month, rule.day);
            return here >= base ? here : clampDay(y + 1, rule.month, rule.day);
        }
    }
}
/** The occurrence after `from` (itself an occurrence), keeping the rule's cadence. */
export function nextOccurrence(rule, from) {
    const [y, m] = parts(from);
    switch (rule.freq) {
        case 'daily':
            return addDaysIso(from, rule.interval);
        case 'weekdays': {
            let d = addDaysIso(from, 1);
            while (!isWeekday(d))
                d = addDaysIso(d, 1);
            return d;
        }
        case 'weekly': {
            const monday = mondayOf(from);
            // Later in the same week, else the first chosen day N weeks on (weeks start on Monday).
            const order = [...rule.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
            const offset = (weekdayOf(from) + 6) % 7;
            const later = order.find((w) => (w + 6) % 7 > offset);
            if (later !== undefined)
                return addDaysIso(monday, (later + 6) % 7);
            return addDaysIso(addDaysIso(monday, 7 * rule.interval), (order[0] + 6) % 7);
        }
        case 'monthly': {
            const [ny, nm] = addMonths(y, m, rule.interval);
            return clampDay(ny, nm, rule.day);
        }
        case 'yearly':
            return clampDay(y + 1, rule.month, rule.day);
    }
}
/** The first occurrence after `floor`, stepping from the occurrence `from` (ticking and skipping use this). */
export function occurrenceAfter(rule, from, floor) {
    let d = from;
    for (let i = 0; i < 10_000 && d <= floor; i++)
        d = nextOccurrence(rule, d);
    return d;
}
/** The latest occurrence on or before `today`, stepping from the occurrence `from` (the roll-over's replacement). */
export function latestOccurrence(rule, from, today) {
    let d = from;
    for (let i = 0; i < 10_000; i++) {
        const n = nextOccurrence(rule, d);
        if (n > today)
            break;
        d = n;
    }
    return d;
}
/**
 * What the next tick or skip moves to: the first occurrence after the later of the occurrence's own date and today. A daily
 * item ticked today comes back tomorrow; a Friday one ticked early on Wednesday comes back the Friday after; a missed one
 * ticked on Saturday comes back next Friday.
 */
export function nextAfterTick(rule, occursOn, today) {
    return occurrenceAfter(rule, occursOn, occursOn > today ? occursOn : today);
}
// --- Words --------------------------------------------------------------------------------------
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function ordinal(n) {
    const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
    return `${n}${s}`;
}
const list = (xs) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);
/** The rule in words: "Every day", "Every weekday", "Every Friday", "Every 2 weeks on Mon and Thu", "Every month on the 15th". */
export function repeatLabel(rule) {
    switch (rule.freq) {
        case 'daily':
            return rule.interval === 1 ? 'Every day' : `Every ${rule.interval} days`;
        case 'weekdays':
            return 'Every weekday';
        case 'weekly': {
            const order = [...rule.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
            if (rule.interval === 1 && order.length === 7)
                return 'Every day';
            const names = order.length === 1 ? [DAY_LONG[order[0]]] : order.map((d) => DAY_SHORT[d]);
            return rule.interval === 1 ? `Every ${list(names)}` : `Every ${rule.interval} weeks on ${list(names)}`;
        }
        case 'monthly': {
            const on = rule.day === 'last' ? 'the last day' : `the ${ordinal(rule.day)}`;
            return rule.interval === 1 ? `Every month on ${on}` : `Every ${rule.interval} months on ${on}`;
        }
        case 'yearly':
            return `Every year on ${rule.day} ${MONTH_LONG[rule.month - 1]}`;
    }
}
// --- Reading "every …" in quick add ---------------------------------------------------------------
const DAY_WORDS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
function dayWord(word) {
    const w = (word ?? '').toLowerCase().replace(/[,]+$/, '').replace(/s$/, '');
    if (w.length < 3)
        return -1;
    const full = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const i = full.findIndex((d) => d.startsWith(w));
    if (i >= 0)
        return i;
    // "tues", "thurs", "weds"
    const alt = { tues: 2, thur: 4, thurs: 4, wed: 3, weds: 3 };
    return alt[w] ?? DAY_WORDS.indexOf(w);
}
function dayOfMonth(word) {
    const m = /^(\d{1,2})(st|nd|rd|th)?$/.exec((word ?? '').toLowerCase());
    if (!m)
        return null;
    const n = Number(m[1]);
    return n >= 1 && n <= 31 ? n : null;
}
/**
 * Reads a repeat from the start of `words`: "every day", "daily", "every weekday", "every monday", "every mon and thu",
 * "every 2 days", "every 2 weeks (on friday)", "every month (on the 15th / on the last day)", "monthly", "every year",
 * "yearly". `today` fills what the phrase leaves open ("every week" is on today's weekday). Null when it isn't one.
 */
export function parseRepeatPrefix(words, today) {
    const w = words.map((x) => x.toLowerCase());
    const [, tm, td] = parts(today);
    const todayWd = weekdayOf(today);
    let i = 0;
    let bare = false;
    let unit = null;
    let interval = 1;
    let days = [];
    let monthDay = null;
    if (w[0] === 'every') {
        i = 1;
        if (w[i] === 'other') {
            interval = 2;
            i++;
        }
        else if (/^\d{1,2}$/.test(w[i] ?? '') && Number(w[i]) >= 1 && Number(w[i]) <= 99) {
            interval = Number(w[i]);
            i++;
        }
        const u = (w[i] ?? '').replace(/s$/, '');
        if (u === 'day')
            unit = 'day';
        else if (u === 'weekday' && interval === 1)
            unit = 'weekday';
        else if (u === 'week')
            unit = 'week';
        else if (u === 'month')
            unit = 'month';
        else if (u === 'year' && interval === 1)
            unit = 'year';
        if (unit)
            i++;
        else if (interval === 1 && dayWord(w[i]) >= 0) {
            // "every monday", "every mon and thu", "every monday, wednesday, friday"
            unit = 'week';
            while (i < w.length) {
                const d = dayWord(w[i]);
                if (d >= 0) {
                    if (!days.includes(d))
                        days.push(d);
                    i++;
                }
                else if ((w[i] === 'and' || w[i] === ',') && dayWord(w[i + 1]) >= 0)
                    i++;
                else
                    break;
            }
        }
        else if (interval === 1 && (dayOfMonth(w[i]) !== null && /(st|nd|rd|th)$/.test(w[i] ?? ''))) {
            // "every 15th"
            unit = 'month';
            monthDay = dayOfMonth(w[i]);
            i++;
        }
        else
            return null;
    }
    else if (w[0] === 'daily')
        unit = 'day';
    else if (w[0] === 'weekly')
        unit = 'week';
    else if (w[0] === 'monthly')
        unit = 'month';
    else if (w[0] === 'yearly' || w[0] === 'annually')
        unit = 'year';
    else
        return null;
    if (w[0] !== 'every') {
        i = 1;
        bare = true;
    }
    // "… on friday", "… on mon and thu" after a week; "… on the 15th", "… on the last day" after a month.
    if (w[i] === 'on') {
        if (unit === 'week' && !days.length && dayWord(w[i + 1]) >= 0) {
            i++;
            while (i < w.length) {
                const d = dayWord(w[i]);
                if (d >= 0) {
                    if (!days.includes(d))
                        days.push(d);
                    i++;
                }
                else if ((w[i] === 'and' || w[i] === ',') && dayWord(w[i + 1]) >= 0)
                    i++;
                else
                    break;
            }
        }
        else if (unit === 'month' && monthDay === null) {
            let j = i + 1;
            if (w[j] === 'the')
                j++;
            if (w[j] === 'last') {
                monthDay = 'last';
                j++;
                if (w[j] === 'day')
                    j++;
                i = j;
            }
            else if (dayOfMonth(w[j]) !== null) {
                monthDay = dayOfMonth(w[j]);
                i = j + 1;
            }
        }
    }
    if (bare && i > 1)
        bare = false;
    let rule;
    switch (unit) {
        case 'day':
            rule = { freq: 'daily', interval };
            break;
        case 'weekday':
            rule = { freq: 'weekdays' };
            break;
        case 'week':
            rule = { freq: 'weekly', interval, days: days.length ? days : [todayWd] };
            break;
        case 'month':
            rule = { freq: 'monthly', interval, day: monthDay ?? td };
            break;
        case 'year':
            rule = { freq: 'yearly', month: tm, day: td };
            break;
        default:
            return null;
    }
    return { rule, consumed: i, bare };
}
// --- Time nudges ----------------------------------------------------------------------------------
/** When an item with a time nudges: at the time, some minutes before, or not at all. */
// Not bare numbers: settings are stored as jsonb, where a string like "30" would read back as a number.
export const TodoNudge = z.enum(['at', '5m', '15m', '30m', 'off']);
export const NUDGE_LABELS = {
    at: 'At the time',
    '5m': '5 minutes before',
    '15m': '15 minutes before',
    '30m': '30 minutes before',
    off: 'Off',
};
export const nudgeMinutesBefore = (n) => (n === 'off' ? null : n === 'at' ? 0 : Number.parseInt(n, 10));
/** The instant an item's nudge goes off: its day and time on the person's clock, minus the early minutes. Null for off. */
export function nudgeInstant(dueOn, dueTime, nudge, timeZone) {
    const before = nudgeMinutesBefore(nudge);
    if (before === null)
        return null;
    const [y, m, d] = parts(dueOn);
    const [h, mi] = dueTime.split(':').map(Number);
    let at = zonedToUtc(y, m, d, h, mi, timeZone);
    // A time inside a spring-forward gap (2:30 am on the day clocks go forward) goes off just after it, never before.
    const seen = zonedParts(at, timeZone);
    const short = h * 60 + mi - (seen.hour * 60 + seen.minute);
    if (seen.day === d && short > 0)
        at = new Date(at.getTime() + short * 60_000);
    return new Date(at.getTime() - before * 60_000);
}
// --- Checklists -----------------------------------------------------------------------------------
export const MAX_TODO_STEPS = 20;
export const STEP_LIMIT_MESSAGE = `A to-do can have up to ${MAX_TODO_STEPS} steps.`;
export const TodoStep = z.object({ id: z.uuid(), text: z.string(), done: z.boolean(), position: z.number() });
const StepText = z.string().trim().min(1).max(200);
export const AddTodoStepInput = z.object({ text: StepText });
export const UpdateTodoStepInput = z
    .object({ text: StepText.optional(), done: z.boolean().optional() })
    .refine((v) => v.text !== undefined || v.done !== undefined, { message: 'Change the text or the tick' });
export const ReorderTodoStepsInput = z.object({ stepIds: z.array(z.uuid()).min(1).max(MAX_TODO_STEPS) });
/** "2/5" on a row with steps; null without. */
export function stepProgress(steps) {
    return steps.length ? `${steps.filter((s) => s.done).length}/${steps.length}` : null;
}
/** Every step ticked while the item is still open: offer "All steps done. Cross it off?". */
export const allStepsDone = (item) => !item.done && item.steps.length > 0 && item.steps.every((s) => s.done);
// --- The "Crossed off today" card ---------------------------------------------------------------------
/**
 * The card's line, worded from today's count first so it never contradicts it: nothing yet (keeping a live streak going),
 * the first ones of a new streak, or a streak of N days.
 */
export function streakMessage(done, streak) {
    if (done <= 0) {
        return streak > 0 ? `Nothing crossed off yet today. Cross one off to keep your ${streak}-day streak going.` : 'Nothing crossed off yet. Cross one thing off to start a streak.';
    }
    if (streak > 1)
        return `${streak}-day streak of crossing things off. Keep it going.`;
    return done === 1 ? 'You crossed off your first one today. That’s a streak started.' : `You crossed off ${done} today. That’s a streak started.`;
}
//# sourceMappingURL=myday-repeat.js.map